import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Seo } from "@/components/Layout";
import { Overline, Breadcrumbs } from "@/components/Bits";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, ShieldCheck, Megaphone, BarChart3, Building2, AlertTriangle } from "lucide-react";

const LEGAL_FIELDS = [
  ["company_name", "Nombre de la empresa"], ["legal_responsible", "Responsable legal"],
  ["address", "Domicilio"], ["tax_id", "Número de identificación fiscal"], ["contact_email", "Correo de contacto oficial"],
];
const ANALYTICS_FIELDS = [
  ["adsense_client_id", "ID de Google AdSense", "ca-pub-XXXXXXXXXXXXXXXX"],
  ["ga_id", "ID de Google Analytics", "G-XXXXXXXXXX"],
  ["gsc_verification", "Verificación Search Console", "código meta"],
];

export default function Admin() {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/site-config").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try { const { data } = await api.put("/admin/site-config", payload); setCfg(data); toast.success("Configuración guardada"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveAll = () => save({
    company_name: cfg.company_name, legal_responsible: cfg.legal_responsible, address: cfg.address,
    tax_id: cfg.tax_id, contact_email: cfg.contact_email, adsense_client_id: cfg.adsense_client_id,
    ga_id: cfg.ga_id, gsc_verification: cfg.gsc_verification,
  });

  if (!cfg) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  const input = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
  const adsReady = !!cfg.adsense_client_id;

  return (
    <>
      <Seo title="Administración" noindex />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
          <Overline>Administración</Overline>
          <h1 className="font-display font-black text-3xl">Configuración del sitio</h1>
          <p className="text-muted-foreground mt-1 text-sm">Completa los datos legales y de servicios externos. La publicidad solo se activa con un identificador válido.</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Administración" }]} />

        <section className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><Building2 size={18} className="text-primary" /><h2 className="font-display font-semibold">Datos legales de la empresa</h2></div>
          <div className="grid sm:grid-cols-2 gap-4">
            {LEGAL_FIELDS.map(([k, label]) => (
              <div key={k}><label className="block text-sm font-medium mb-1.5">{label}</label>
                <input className={input} value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} data-testid={`legal-${k}`} placeholder="Pendiente de completar" /></div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-accent" /><h2 className="font-display font-semibold">Analítica y verificación</h2></div>
          <div className="grid sm:grid-cols-2 gap-4">
            {ANALYTICS_FIELDS.map(([k, label, ph]) => (
              <div key={k}><label className="block text-sm font-medium mb-1.5">{label}</label>
                <input className={input} value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} data-testid={`analytics-${k}`} placeholder={ph} /></div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><Megaphone size={18} className="text-primary" /><h2 className="font-display font-semibold">Publicidad (Google AdSense)</h2></div>
          {!adsReady && (
            <div className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400 flex gap-2 mb-4">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />Introduce un ID de AdSense válido arriba para poder activar la publicidad.
            </div>
          )}
          <div className="flex items-center justify-between">
            <div><p className="font-medium text-sm">Mostrar anuncios</p><p className="text-xs text-muted-foreground">Solo en guías editoriales extensas y páginas públicas con suficiente contenido.</p></div>
            <Switch checked={!!cfg.ads_enabled} disabled={!adsReady} data-testid="ads-toggle"
              onCheckedChange={(v) => { setCfg({ ...cfg, ads_enabled: v }); save({ ads_enabled: v }); }} />
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-accent" /><h2 className="font-display font-semibold">Estado de configuración</h2></div>
          <div className="grid sm:grid-cols-2 gap-2">
            {cfg.pending_legal?.map((p, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span>{p.field}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "completo" ? "bg-emerald-500/10 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </section>

        <Button onClick={saveAll} className="rounded-full" data-testid="admin-save"><Save size={16} className="mr-1" />Guardar cambios</Button>
      </div>
    </>
  );
}
